import type {FieldTypeId} from 'src/typings';
import {SUTOR_REFERENCE_STATES, SUTOR_REPORT_STATES} from 'src/features/erp-sutor/sutor-constants';

export const FOND_PROJECT = {
    fond: 1,

    // All supported project subfonds
    subfonds: [4, 48, 49],

    projectNameFieldId: 'd245.a' as FieldTypeId,
    projectNumberFieldId: 'd245.z' as FieldTypeId,

    // Subfonds
    REPORTED: {
        fond: 5
    },
    CONTRACTED: {
        fond: 4
    },
    SERVICE_PROJECT: {
        fond: 48
    },
    CALCULATION_PROJECT: {
        fond: 49
    }
};

export const FOND_REFERENCE = {
    fond: 80,

    nameFieldId: 'd8000.a' as FieldTypeId,
    stateFieldId: 'd8005.h' as FieldTypeId,
    userFieldId: 'd8010.x' as FieldTypeId,
    creationDateFieldId: 'd8015.d' as FieldTypeId,
    dateFieldId: 'd8016.d' as FieldTypeId,
    totalHoursFieldId: 'd8020.m' as FieldTypeId,

    // Reference state chip styles
    STATE_CHIP_STYLES: {
        [SUTOR_REFERENCE_STATES.NEW]: 'brand-orange',
        [SUTOR_REFERENCE_STATES.DONE]: 'success',
        [SUTOR_REFERENCE_STATES.IN_PROGRESS]: 'accent-blue'
    } as const
};

export const FOND_PERSON = {
    fond: 7
};

export const FOND_MONTHLY_ATTENDANCE = {
    fond: 89,

    employeeFieldId: 'd8980.x' as FieldTypeId,
    personNumberFieldId: 'd8980.o' as FieldTypeId,
    contractTypeFieldId: 'd8981.e' as FieldTypeId,
    divisionFieldId: 'd8983.a' as FieldTypeId,
    yearMonthFieldId: 'd8907.a' as FieldTypeId,
    superiorFieldId: 'd8984.x' as FieldTypeId,
    stateFieldId: 'd8905.s' as FieldTypeId,
    employerFieldId: 'd8982.a' as FieldTypeId,
    textNoteFieldId: 'd8990.t' as FieldTypeId,

    overtimePaymentStateFieldId: 'd8920.a' as FieldTypeId,
    onCallPaymentStateFieldId: 'd8920.b' as FieldTypeId,
    idpPaymentStateFieldId: 'd8920.c' as FieldTypeId
};

export const FOND_INSTALLATION_LOGBOOK = {
    fond: 30,

    dateFieldId: 'd2030.d' as FieldTypeId,
    customerProjectFieldId: 'd1670.z' as FieldTypeId,
    parentItemFieldId: 'd3002.z' as FieldTypeId
};

export const FOND_PROJECT_BUSINESS_ITEM = {
    fond: 75,

    subfonds: [76, 77],

    OWN_WORK_ITEM: {
        fond: 76
    },
    FIXED_PRICE_ITEM: {
        fond: 77
    }
};

export const FOND_REPORT = {
    fond: 10,

    subfondsForOptimisation: [20, 21, 22],

    subfondsForApproval: [20, 21, 22, 26, 47, 37, 38, 44, 45, 800],
    subfondsForLogbookPairing: [20, 21, 22, 47, 38, 44, 45, 24 /* Optimisation reports */, 800],

    thingReports: [38, 44, 45],

    // Shared fields
    nameFieldId: 'd245.a' as FieldTypeId,
    topProjectFieldId: 'd1001.a' as FieldTypeId,
    closestProjectFieldId: 'd1005.a' as FieldTypeId,
    projectFieldId: 'd1002.main' as FieldTypeId,
    dateFieldId: 'd2030.d' as FieldTypeId,
    stateFieldId: 'd2096.s' as FieldTypeId,
    customerProjectMainFieldId: 'd1670.main' as FieldTypeId,
    customerProjectFieldId: 'd1670.z' as FieldTypeId,
    textNoteFieldId: 'd2091.a' as FieldTypeId,
    referenceFieldId: 'd2012.a' as FieldTypeId,
    installationLogbookFieldId: 'd2031.a' as FieldTypeId,
    workerFieldId: 'd2051.x' as FieldTypeId,
    workerMainFieldId: 'd2051.main' as FieldTypeId,
    machineFieldId: 'd2060.a' as FieldTypeId,
    divisionFieldId: 'd2037.a' as FieldTypeId,
    reportedPersonStandardFieldId: 'd2070.m' as FieldTypeId,
    reportedPersonOvertimeFieldId: 'd2070.n' as FieldTypeId,
    reportedMachineFieldId: 'd2070.o' as FieldTypeId,
    chargedPersonStandardFieldId: 'd2080.m' as FieldTypeId,
    chargedPersonOvertimeFieldId: 'd2080.n' as FieldTypeId,
    chargedMachineFieldId: 'd2080.o' as FieldTypeId,
    chargedMachineOvertimeFieldId: 'd2080.p' as FieldTypeId,
    personUnitFieldId: 'd2054.j' as FieldTypeId,
    machineUnitFieldId: 'd2064.j' as FieldTypeId,
    responsiblePersonFieldId: 'd2013.x' as FieldTypeId,

    // For 38, 44, 45
    sellPriceFieldId: 'd2082.c' as FieldTypeId,
    sellCountFieldId: 'd2082.m' as FieldTypeId,
    sellUnitFieldId: 'd2075.j' as FieldTypeId,

    // Report state chip styles
    STATE_CHIP_STYLES: {
        [SUTOR_REPORT_STATES.TO_CONFIRM]: 'accent-blue',
        [SUTOR_REPORT_STATES.CONFIRMED]: 'success',
        [SUTOR_REPORT_STATES.RETURNED]: 'danger'
    } as const,

    SHORTHAND_NAMES: {
        20: 'Stroj s obsl.',
        21: 'Práce',
        22: 'Stroj',
        26: 'Absence',
        47: 'Pohotovost',
        37: 'Režie',
        38: 'Dílo',
        44: 'Materiál',
        45: 'Subdodávka',
        24: 'Optimizace',
        800: 'Vývoj systému'
    },

    // Subfonds
    MACHINE_WITH_OPERATOR: {
        fond: 20
    },
    WORK: {
        fond: 21,

        typeFieldId: 'd2035.t' as FieldTypeId,
        placeFieldId: 'd2094.a' as FieldTypeId,
        partnerFieldId: 'd2093.a' as FieldTypeId,
        jobTypeFieldId: 'd2040' as FieldTypeId
    },
    MACHINE: {
        fond: 22
    },
    ABSENCE: {
        fond: 26,

        typeFieldId: 'd2035.t' as FieldTypeId
    },
    OPERATION: {
        fond: 37
    },
    EXECUTED_WORK: {
        fond: 38
    },
    MATERIAL: {
        fond: 44
    },
    SUBSUPPLY: {
        fond: 45
    },
    OPTIMISATION: {
        fond: 24
    },
    SYSTEM_DEVELOPMENT: {
        fond: 800
    }
};

export const FOND_JOB_FILE = {
    fond: 83,

    birthNumberFieldId: 'd1845.b' as FieldTypeId,
    identityCardNumberFieldId: 'd1870.m' as FieldTypeId,
    genderFieldId: 'd1845.l' as FieldTypeId,
    birthDateFieldId: 'd1845.a' as FieldTypeId,
    birthPlaceFieldId: 'd1845.c' as FieldTypeId,
    nationalityFieldId: 'd1845.f' as FieldTypeId,
    idCardNumberFieldId: 'd1870.d' as FieldTypeId,
    idCardValidityFieldId: 'd1870.e' as FieldTypeId,
    educationLevelFieldId: 'd1845.j' as FieldTypeId,
    educationInstitutionFieldId: 'd1845.k' as FieldTypeId,
    healthInsuranceCodeFieldId: 'd1870.g' as FieldTypeId,
    maternityLeaveStartFieldId: 'd1860.a' as FieldTypeId,
    maternityLeaveEndFieldId: 'd1860.b' as FieldTypeId,
    parentalLeaveStartFieldId: 'd1860.c' as FieldTypeId,
    parentalLeaveEndFieldId: 'd1860.d' as FieldTypeId,
    invalidityFieldId: 'd1870.i' as FieldTypeId,
    claimedChildrenFieldId: 'd1860.e' as FieldTypeId,
    unclaimedChildrenFieldId: 'd1860.f' as FieldTypeId,
    distrainmentTypeFieldId: 'd1860.g' as FieldTypeId // Exekuce
};

export const FOND_EMPLOYMENT_STATUS = {
    fond: 88,

    personalNumberFieldId: 'd1810.o' as FieldTypeId,
    employmentStartDateFieldId: 'd1820.b' as FieldTypeId,
    trialPeriodEndDateFieldId: 'd1820.c' as FieldTypeId,
    employmentEndDateFieldId: 'd1820.d' as FieldTypeId,
    workPositionFieldId: 'd1825.a' as FieldTypeId,
    divisionFieldId: 'd1851.a' as FieldTypeId,
    bossFieldId: 'd1852.x' as FieldTypeId,
    contractTypeFieldId: 'd1820.e' as FieldTypeId,
    contractDateFieldId: 'd1820.f' as FieldTypeId,
    contractCategoryFieldId: 'd1820.k' as FieldTypeId,
    employerFieldId: 'd1850.a' as FieldTypeId,
    employerIcoFieldId: 'd1850.i' as FieldTypeId,
    typeOfTerminationFieldId: 'd1861.b' as FieldTypeId,
    sideOfTerminationFieldId: 'd1861.a' as FieldTypeId,
    reasonForTerminationFieldId: 'd1861.c' as FieldTypeId,
    textNoteFieldId: 'd1890.t' as FieldTypeId
};
